import { alpha, Box } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ALPHA, CONTROL_HEIGHT, FONT_SIZE, INK } from "../../../theme/tokens";
import type { RequestOriginGroup } from "../../../utils/models";
import { ORIGIN_GROUPS } from "../../../utils/requestOrigins";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { AccentIconTile } from "../../UIToolkit/molecules/AccentIconTile";
import { ORIGIN_GROUP_ICON, ORIGIN_GROUP_TONE, type OriginFilterControl, RequestOriginFilterLayout } from "./requestOriginMeta";
import { useOriginFilterChange } from "./useOriginFilterChange";

type Props = { control: OriginFilterControl };

/**
 * "Origin: All ▾" — a compact multi-select over Automation / People / External, each with how many of the listed
 * requests it holds. Once set, the trigger is tinted and names the choice, so a narrowed list never passes for all.
 */
export const RequestOriginFilter: FC<Props> = ({ control }) => {
	const { t } = useTranslation();
	const onChange = useOriginFilterChange(control, RequestOriginFilterLayout.Classic);
	const items = useMemo<DropdownItem<RequestOriginGroup>[]>(
		() =>
			ORIGIN_GROUPS.map((group) => {
				const Icon = ORIGIN_GROUP_ICON[group];
				return {
					value: group,
					id: group,
					label: t(`request.origin.group.${group}`),
					secondaryLabel: t(`request.origin.groupHint.${group}`),
					icon: (
						<AccentIconTile size="xs" tone={ORIGIN_GROUP_TONE[group]} component="span">
							<Icon />
						</AccentIconTile>
					),
					endAdornment: (
						<Box component="span" sx={{ color: INK.MUTED, fontSize: FONT_SIZE.SECONDARY, fontVariantNumeric: "tabular-nums" }}>
							{control.counts[group]}
						</Box>
					),
				};
			}),
		[t, control.counts],
	);
	const isNarrowed = control.selected.length > 0;
	const value = isNarrowed ? control.selected.map((group) => t(`request.origin.group.${group}`)).join(", ") : t("request.origin.filter.all");

	return (
		<SystemDropdown
			data-testid="request-origin-filter"
			multiple
			items={items}
			selectedItems={[...control.selected]}
			onChangeMultiple={onChange}
			trigger="button"
			hideTriggerCount
			search={false}
			label={t("request.origin.filter.trigger", { value })}
			selectSx={(theme) => ({
				height: CONTROL_HEIGHT.COMPACT,
				py: 0,
				gap: 0.5,
				whiteSpace: "nowrap",
				...(isNarrowed && {
					borderColor: theme.palette.primary.main,
					color: theme.palette.primary.dark,
					fontWeight: 600,
					backgroundColor: alpha(theme.palette.primary.main, ALPHA.SELECTED_OVERLAY),
				}),
			})}
		/>
	);
};
